import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Landmark, Lock, ShieldCheck } from "lucide-react";
import { getApiErrorMessage } from "@/lib/api";
import { EmailOtpStep } from "@/components/EmailOtpStep";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [setupToken, setSetupToken] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [challenge, setChallenge] = useState(null);
  const { login, completeSetup, verifyTwoFactor, resendTwoFactor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("setup");
    if (token) {
      setSetupToken(token);
      setEmail(params.get("email") || "your registered e-mail");
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.password_setup_required) {
        setSetupToken(data.setup_token);
        toast.info("First login — create your password now (valid 30 minutes).");
      } else if (data.two_factor_required) {
        setChallenge(data);
      } else {
        navigate(location.state?.from?.pathname || "/", { replace: true });
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const data = await completeSetup(setupToken, newPw);
      if (data?.two_factor_required) {
        setSetupToken(null); setChallenge(data);
        toast.success("Password created. Verify the code sent to your e-mail.");
      } else {
        toast.success("Password created — welcome to SMIFS!");
        navigate("/", { replace: true });
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err));
      if ((getApiErrorMessage(err) || "").includes("again")) setSetupToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      <div className="smifs-hero-gradient relative hidden flex-col justify-between p-10 text-white lg:flex">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
            <Landmark className="h-5 w-5 text-white" />
          </div>
          <span className="font-heading text-xl">SMIFS</span>
        </div>
        <div className="max-w-md">
          <h1 className="font-heading text-4xl leading-tight">A Legacy Built on Trust</h1>
<p className="mt-4 text-white/80">Guided by research. Sustained through market cycles. Invest in mutual funds through SMIFS, connected directly to BSE StAR MF.</p>
          <div className="mt-8 flex items-center gap-2 text-sm text-white/70">
            <ShieldCheck className="h-4 w-4" /> Trusted since 1993
          </div>
        </div>
        <div className="text-xs text-white/50">Orders are processed via BSE StAR MF; timelines may vary by AMC/RTA.</div>
      </div>

      <div className="flex items-center justify-center p-3 sm:p-6">
        <div className="glass-card w-full max-w-md p-6 sm:p-8">
          <h2 className="font-heading text-2xl text-[var(--brand)]">{challenge ? "E-mail verification" : setupToken ? "Create your password" : "Welcome back"}</h2>
          {!setupToken && !challenge && <p className="mt-1 text-sm text-[var(--ink-muted)]">Log in to manage your mutual fund investments.</p>}

          {challenge ? <EmailOtpStep challenge={challenge} onVerify={async (code) => { await verifyTwoFactor(challenge.challenge_id, code); toast.success("Sign-in verified"); navigate(location.state?.from?.pathname || "/", { replace: true }); }} onResend={async () => { const next = await resendTwoFactor(challenge.challenge_id); setChallenge(next); return next; }} onCancel={() => { setChallenge(null); setPassword(""); }} /> : setupToken ? (
            <form className="mt-6 space-y-4" onSubmit={handleSetup}>
              <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800" data-testid="client-setup-notice">
                Activate portfolio access for <b>{email}</b>. Set a password (10+ characters, with upper, lower and a digit). This one-time link expires at the time stated in your e-mail.
              </p>
              <div className="space-y-1.5">
                <Label>New password</Label>
                <Input type="password" required value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="client-setup-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Confirm password</Label>
                <Input type="password" required value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="client-setup-confirm-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="client-setup-submit-button">
                <ShieldCheck className="mr-2 h-4 w-4" /> {loading ? "Saving..." : "Create password & sign in"}
              </Button>
            </form>
          ) : (
          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="login-submit-button">
              <Lock className="mr-2 h-4 w-4" /> {loading ? "Signing in..." : "Log in"}
            </Button>
          </form>
          )}

          {!setupToken && !challenge && (
          <p className="mt-6 text-center text-sm text-[var(--ink-muted)]">
            New to SMIFS? <Link to="/signup" className="font-medium text-[var(--cta)]" data-testid="go-to-signup-link">Create an account</Link>
            <span className="mx-1">·</span>
            <Link to="/research" className="font-medium text-[var(--cta)]" data-testid="go-to-research-link">Explore Fund Research</Link>
          </p>
          )}
        </div>
      </div>
    </div>
  );
}
